import { db } from '../firebase';
import { ProposalTemplate, ProposalCompanyInfo, SavedProposal, SavedClient, ProposalCounter } from '../types';

// Firestore service for cloud-based data storage
// Collections structure: users/{userId}/proposals, users/{userId}/templates, users/{userId}/companyInfo

// --- Commercial Proposals ---

export const getProposals = async (userId: string): Promise<SavedProposal[]> => {
    try {
        const snapshot = await db.collection('users').doc(userId).collection('proposals').orderBy('date', 'desc').get();
        return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as SavedProposal));
    } catch (error) {
        console.error('Error fetching proposals:', error);
        return [];
    }
};

export const addProposal = async (userId: string, proposalData: Omit<SavedProposal, 'id'>): Promise<SavedProposal> => {
    try {
        const newProposal = {
            ...proposalData,
            date: new Date().toISOString()
        };
        const docRef = await db.collection('users').doc(userId).collection('proposals').add(newProposal);
        return { id: docRef.id, ...newProposal } as SavedProposal;
    } catch (error) {
        console.error('Error adding proposal:', error);
        throw error;
    }
};

export const updateProposal = async (userId: string, proposalId: string, proposalData: Partial<SavedProposal>): Promise<void> => {
    try {
        await db.collection('users').doc(userId).collection('proposals').doc(proposalId).update(proposalData);
    } catch (error) {
        console.error('Error updating proposal:', error);
        throw error;
    }
};

export const deleteProposal = async (userId: string, proposalId: string): Promise<void> => {
    try {
        await db.collection('users').doc(userId).collection('proposals').doc(proposalId).delete();
    } catch (error) {
        console.error('Error deleting proposal:', error);
        throw error;
    }
};

// --- Proposal Templates (SHARED) ---

export const getProposalTemplates = async (): Promise<ProposalTemplate[]> => {
    try {
        const snapshot = await db.collection('sharedTemplates').get();
        return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as ProposalTemplate));
    } catch (error) {
        console.error('Error fetching templates:', error);
        return [];
    }
};

export const addProposalTemplate = async (userId: string, userEmail: string, templateData: Omit<ProposalTemplate, 'id'>): Promise<ProposalTemplate> => {
    try {
        const newTemplate = {
            ...templateData,
            createdBy: userEmail,
            createdByUid: userId,
            createdAt: new Date().toISOString()
        };
        const docRef = await db.collection('sharedTemplates').add(newTemplate);
        return { id: docRef.id, ...newTemplate } as ProposalTemplate;
    } catch (error) {
        console.error('Error adding template:', error);
        throw error;
    }
};

export const deleteProposalTemplate = async (templateId: string): Promise<void> => {
    try {
        await db.collection('sharedTemplates').doc(templateId).delete();
    } catch (error) {
        console.error('Error deleting template:', error);
        throw error;
    }
};

// --- Company Info (SHARED) ---

export const getCompanyDetails = async (): Promise<ProposalCompanyInfo | null> => {
    try {
        const doc = await db.collection('companySettings').doc('default').get();
        return doc.exists ? (doc.data() as ProposalCompanyInfo) : null;
    } catch (error) {
        console.error('Error fetching company details:', error);
        return null;
    }
};

export const updateCompanyDetails = async (companyInfo: ProposalCompanyInfo, userEmail: string): Promise<void> => {
    try {
        const updatedInfo = {
            ...companyInfo,
            updatedBy: userEmail,
            updatedAt: new Date().toISOString()
        };
        await db.collection('companySettings').doc('default').set(updatedInfo);
    } catch (error) {
        console.error('Error updating company details:', error);
        throw error;
    }
};

// --- Client Database ---

export const getClients = async (userId: string): Promise<SavedClient[]> => {
    try {
        const snapshot = await db.collection('users').doc(userId).collection('clients').orderBy('company', 'asc').get();
        return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as SavedClient));
    } catch (error) {
        console.error('Error fetching clients:', error);
        return [];
    }
};

export const addClient = async (userId: string, clientData: Omit<SavedClient, 'id'>): Promise<SavedClient> => {
    try {
        const newClient = {
            ...clientData,
            createdAt: new Date().toISOString()
        };
        const docRef = await db.collection('users').doc(userId).collection('clients').add(newClient);
        return { id: docRef.id, ...newClient } as SavedClient;
    } catch (error) {
        console.error('Error adding client:', error);
        throw error;
    }
};

export const updateClient = async (userId: string, clientId: string, clientData: Partial<SavedClient>): Promise<void> => {
    try {
        await db.collection('users').doc(userId).collection('clients').doc(clientId).update({
            ...clientData,
            updatedAt: new Date().toISOString()
        });
    } catch (error) {
        console.error('Error updating client:', error);
        throw error;
    }
};

export const deleteClient = async (userId: string, clientId: string): Promise<void> => {
    try {
        await db.collection('users').doc(userId).collection('clients').doc(clientId).delete();
    } catch (error) {
        console.error('Error deleting client:', error);
        throw error;
    }
};

// --- Proposal Auto-Numbering ---

export const getNextProposalNumber = async (userId: string): Promise<string> => {
    const currentYear = new Date().getFullYear();
    const counterRef = db.collection('users').doc(userId).collection('settings').doc('proposalCounter');

    try {
        const doc = await counterRef.get();
        let counter: ProposalCounter;

        if (doc.exists) {
            counter = doc.data() as ProposalCounter;
            // Reset counter if new year
            if (counter.year !== currentYear) {
                counter = { year: currentYear, lastNumber: 0 };
            }
        } else {
            counter = { year: currentYear, lastNumber: 0 };
        }

        // Increment
        counter.lastNumber += 1;
        await counterRef.set(counter);

        // Format: KP-2024-001
        const paddedNumber = counter.lastNumber.toString().padStart(3, '0');
        return `KP-${currentYear}-${paddedNumber}`;
    } catch (error) {
        console.error('Error getting next proposal number:', error);
        // Fallback to timestamp-based number
        return `KP-${currentYear}-${Date.now().toString().slice(-6)}`;
    }
};

// --- Copy Proposal ---

export const copyProposal = async (userId: string, proposalId: string): Promise<SavedProposal> => {
    try {
        const docRef = await db.collection('users').doc(userId).collection('proposals').doc(proposalId).get();
        if (!docRef.exists) {
            throw new Error('Proposal not found');
        }

        const originalData = docRef.data();
        if (!originalData) {
            throw new Error('Proposal data is empty');
        }

        const newNumber = await getNextProposalNumber(userId);

        // Create new proposal without the id field
        const copiedProposal: Omit<SavedProposal, 'id'> = {
            date: new Date().toISOString(),
            sections: originalData.sections,
            clientInfo: {
                ...originalData.clientInfo,
                proposalNumber: newNumber,
                date: new Date().toISOString().split('T')[0]
            },
            discount: originalData.discount,
            vat: originalData.vat,
            total: originalData.total,
            status: 'draft',
            parentId: proposalId,
            version: (originalData.version || 1) + 1
        };

        const newDocRef = await db.collection('users').doc(userId).collection('proposals').add(copiedProposal);
        return { id: newDocRef.id, ...copiedProposal } as SavedProposal;
    } catch (error) {
        console.error('Error copying proposal:', error);
        throw error;
    }
};

// --- Version History ---

export const getProposalVersions = async (userId: string, proposalId: string): Promise<SavedProposal[]> => {
    try {
        // Get current proposal
        const currentDoc = await db.collection('users').doc(userId).collection('proposals').doc(proposalId).get();
        if (!currentDoc.exists) return [];

        const currentProposal = { id: currentDoc.id, ...currentDoc.data() } as SavedProposal;

        // Find the root proposal (original)
        let rootId = proposalId;
        if (currentProposal.parentId) {
            rootId = currentProposal.parentId;
        }

        // Get all versions (proposals with same parentId or the original)
        const snapshot = await db.collection('users').doc(userId).collection('proposals')
            .where('parentId', '==', rootId)
            .orderBy('date', 'desc')
            .get();

        const versions = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as SavedProposal));

        // Add the original if it's not in the list
        if (!versions.find(v => v.id === rootId)) {
            const rootDoc = await db.collection('users').doc(userId).collection('proposals').doc(rootId).get();
            if (rootDoc.exists) {
                versions.push({ id: rootDoc.id, ...rootDoc.data() } as SavedProposal);
            }
        }

        // Sort by version/date
        return versions.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
    } catch (error) {
        console.error('Error fetching proposal versions:', error);
        return [];
    }
};
